import React, { useState } from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import { formatDistanceToNowStrict } from 'date-fns';
import ChevronLeftCircleIcon from '@atlaskit/icon/glyph/chevron-left-circle';
import ChevronRightCircleIcon from '@atlaskit/icon/glyph/chevron-right-circle';
import Button from '@atlaskit/button';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import Tooltip from '@atlaskit/tooltip';
import { getMinimumFractionDigitsByCurrency } from '../../formattedV2';
import './collapsible-pricing-estimate.scss';

interface CollapsiblePricingEstimateProps {
  title: { label: string; value?: number; subValue?: React.ReactNode };
  data: Array<{ label: string; value?: number }>;
  currency: string;
  onRefreshClick?: () => void;
  showRefreshOption?: boolean;
  hasProductEnrichmentFailed?: boolean;
  isProductEnrichmentPending?: boolean;
  hideLastRefreshed?: boolean;
  showEmptyValuesInAmounts?: boolean;
  expandAndCollapseIconProps?: { [key: string]: any };
}

const getFormattedAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', getMinimumFractionDigitsByCurrency(currency)).format(
    Number(Number(amount).toFixed(2))
  );

export const getFormattedCurrency = (
  value: number | undefined,
  currency: string,
  showEmptyValuesInAmounts?: boolean
) => {
  if (isNil(value)) {
    return (
      <>
        {currency} {showEmptyValuesInAmounts ? getFormattedAmount(0, currency) : '...'}
      </>
    );
  }
  return (
    <>
      {currency} {getFormattedAmount(value, currency)}
    </>
  );
};

export const CollapsiblePricingEstimate = ({
  title,
  data,
  currency,
  onRefreshClick,
  showRefreshOption = false,
  hasProductEnrichmentFailed = false,
  isProductEnrichmentPending = false,
  hideLastRefreshed = false,
  showEmptyValuesInAmounts = false,
  expandAndCollapseIconProps = {},
}: CollapsiblePricingEstimateProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastRefreshed, setLastRefresh] = useState<Date | null>(null);

  const _onRefreshClick = () => {
    setLastRefresh(new Date());
    onRefreshClick && onRefreshClick();
  };

  const renderRefreshButton = () => {
    const refreshBtn = (
      <Button
        iconBefore={<RefreshIcon label="Refresh Estimate" />}
        onClick={_onRefreshClick}
        testId="collapsible-pricing-refresh-btn"
        isDisabled={hasProductEnrichmentFailed || isProductEnrichmentPending}
      >
        Refresh
      </Button>
    );

    if (!showRefreshOption) return null;

    let tooltipContent = null;
    if (hasProductEnrichmentFailed) {
      tooltipContent = (
        <div>One or more items in the cart has failed to load. Please check the banner above to continue.</div>
      );
    }

    if (isProductEnrichmentPending) {
      tooltipContent = <div>We are getting additional details for one or more items in the cart. Please wait.</div>;
    }

    if (hasProductEnrichmentFailed || isProductEnrichmentPending) {
      return (
        <Tooltip content={tooltipContent} tag="span">
          {refreshBtn}
        </Tooltip>
      );
    }

    return refreshBtn;
  };

  const renderLastRefresh = () => {
    return showRefreshOption ? (
      <div className={classnames('collapsible-summary-grid-v2-last-refresh', { 'value-empty': !lastRefreshed })}>
        Last refreshed {lastRefreshed ? formatDistanceToNowStrict(new Date(lastRefreshed), { addSuffix: true }) : '...'}
      </div>
    ) : null;
  };

  const renderTitleItem = () => {
    return (
      <>
        <div className="title-label">{title.label}</div>
        <div className="title-value">{getFormattedCurrency(title.value, currency, showEmptyValuesInAmounts)}</div>
        {title.subValue && <div className="title-sub-value">{title.subValue}</div>}
      </>
    );
  };

  return (
    <div className="collapsible-summary-grid-v2">
      <div className="collapsible-summary-grid-v2-container">
        {!isExpanded && (
          <>
            <div>
              <div
                className="summary-header collapsed"
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid="expand-section"
              >
                <>
                  <div className="expand-icon">
                    <ChevronLeftCircleIcon {...expandAndCollapseIconProps} label="Expand icon" />
                  </div>
                  <div className={classnames('title-collapsed', { 'value-empty': isNil(title.value) })}>
                    {renderTitleItem()}
                  </div>
                </>
              </div>
              {!hideLastRefreshed && renderLastRefresh()}
            </div>
            {renderRefreshButton()}
          </>
        )}

        {isExpanded && (
          <>
            <div>
              <div className="summary-fields">
                <Button
                  className="collapse-icon"
                  appearance="subtle"
                  testId="collapse-icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  iconBefore={<ChevronRightCircleIcon {...expandAndCollapseIconProps} label="Collapse Icon" />}
                />
                {data.map((fieldDetail, idx) => {
                  return (
                    <div className={classnames('row', { 'value-empty': isNil(fieldDetail.value) })} key={idx}>
                      <div className="row-title">{fieldDetail.label}</div>
                      <div className="row-value">
                        {getFormattedCurrency(fieldDetail.value, currency, showEmptyValuesInAmounts)}
                      </div>
                    </div>
                  );
                })}
                <div className={classnames('row', { 'value-empty': isNil(title.value) })}>{renderTitleItem()}</div>
              </div>
              {!hideLastRefreshed && renderLastRefresh()}
            </div>
            {renderRefreshButton()}
          </>
        )}
      </div>
    </div>
  );
};
